import { useState, useRef, useCallback, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { Waypoint, ManualWaypoint, RouteOptions } from '@/types/waypoint';
import { toast } from 'sonner';
import { getDirections, formatDistance, formatDuration, DirectionsRoute } from '@/services/mapboxDirections';
import { getElevationProfile } from '@/services/elevationService';

interface WaypointManagerProps {
  map: mapboxgl.Map | null;
  onRouteUpdate?: (waypoints: Waypoint[]) => void;
}

export function useWaypointManager({ map, onRouteUpdate }: WaypointManagerProps) {
  const [waypoints, setWaypoints] = useState<Waypoint[]>([]);
  const [manualWaypoints, setManualWaypoints] = useState<ManualWaypoint[]>([]);
  const [origin, setOrigin] = useState<Waypoint | null>(null);
  const [destination, setDestination] = useState<Waypoint | null>(null);
  const [isAddingMode, setIsAddingMode] = useState(false);
  const [isManualMode, setIsManualMode] = useState(false);
  const [originLocked, setOriginLocked] = useState(false);
  const [destinationLocked, setDestinationLocked] = useState(false);
  const [currentRoute, setCurrentRoute] = useState<DirectionsRoute | null>(null);
  const [routeProfile, setRouteProfile] = useState<'driving' | 'walking' | 'cycling'>('driving');
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);
  const [elevationData, setElevationData] = useState<Array<{distance: number, elevation: number}>>([]);
  
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const routeLayerRef = useRef<string>('route-layer');
  const mapRef = useRef<mapboxgl.Map | null>(map);
  const modesRef = useRef({ isAddingMode, isManualMode });
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update refs when values change
  useEffect(() => {
    mapRef.current = map;
  }, [map]);
  
  useEffect(() => {
    modesRef.current = { isAddingMode, isManualMode };
    
    console.log('🎯 Waypoint mode changed:', { isAddingMode, isManualMode });
    
    // Update cursor when modes change
    if (map) {
      const canvas = map.getCanvas();
      if (canvas) {
        if (isAddingMode || isManualMode) {
          canvas.style.cursor = 'crosshair';
          console.log('✅ Cursor set to crosshair for waypoint adding');
        } else {
          canvas.style.cursor = '';
          console.log('🔄 Cursor reset to default');
        }
      }
    } else {
      console.log('⚠️ No map available for cursor change');
    }
  }, [isAddingMode, isManualMode, map]);

  // Reverse geocode coordinates to get place name
  const reverseGeocode = async (coords: [number, number]): Promise<string> => {
    try {
      // Get token from localStorage or environment
      const token = localStorage.getItem('mapbox-token') || import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
      if (!token) {
        console.warn('No Mapbox token for geocoding');
        return `${coords[1].toFixed(4)}, ${coords[0].toFixed(4)}`;
      }
      
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${coords[0]},${coords[1]}.json?access_token=${token}`
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        return data.features[0].place_name || 'Unknown location';
      }
      return 'Unknown location';
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return 'Unknown location';
    }
  };

  // Clear all markers from map
  const clearMarkers = useCallback(() => {
    console.log(`🧹 Clearing ${markersRef.current.length} existing markers`);
    markersRef.current.forEach((marker, index) => {
      try {
        marker.remove();
        console.log(`✅ Removed marker ${index + 1}`);
      } catch (error) {
        console.warn(`⚠️ Error removing marker ${index + 1}:`, error);
      }
    });
    markersRef.current = [];
  }, []);

  // Add a waypoint marker to the map
  const addWaypointMarker = useCallback((waypoint: Waypoint | ManualWaypoint, index: number, totalWaypoints: number) => {
    if (!map) return null;

    const coords: [number, number] = 'coords' in waypoint 
      ? waypoint.coords 
      : [waypoint.longitude, waypoint.latitude];

    // Create marker element
    const el = document.createElement('div');
    el.className = 'waypoint-marker';
    
    // Determine actual type based on position for regular waypoints
    let displayType = 'waypoint';
    let displayLabel = String(index + 1);
    
    if ('type' in waypoint) {
      // Use the waypoint's actual type (set by Komoot-style logic)
      if (waypoint.type === 'origin') {
        displayType = 'origin';
        displayLabel = 'A';
      } else if (waypoint.type === 'destination') {
        displayType = 'destination';
        displayLabel = 'B';
      } else {
        displayType = 'waypoint';
        displayLabel = String(index);
      }
      
      // Create nested HTML structure for reliable text rendering
      switch (displayType) {
        case 'origin':
          // STEP 2 FIX: Enhanced origin marker with guaranteed label visibility
          el.className = 'labeled-marker-container step2-origin-marker';
          el.style.cssText = `
            position: relative;
            width: 36px;
            height: 36px;
            z-index: 1000;
          `;

          // Create the marker circle with enhanced visibility
          const originMarker = document.createElement('div');
          originMarker.className = 'custom-marker origin-marker';
          originMarker.style.cssText = `
            width: 36px;
            height: 36px;
            background: #059669;
            border: 3px solid white;
            border-radius: 50%;
            box-shadow: 0 4px 8px rgba(0,0,0,0.4), 0 0 0 1px rgba(5,150,105,0.3);
            cursor: pointer;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
          `;

          // Create the label element with maximum visibility
          const originLabel = document.createElement('div');
          originLabel.className = 'marker-label origin-label';
          originLabel.innerText = displayLabel;
          originLabel.style.cssText = `
            color: white;
            font-size: 18px;
            font-weight: 900;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
            text-align: center;
            pointer-events: none;
            user-select: none;
            line-height: 1;
            text-shadow: 0 1px 2px rgba(0,0,0,0.8);
            letter-spacing: 0.5px;
            z-index: 1001;
            position: relative;
          `;

          // Combine elements
          originMarker.appendChild(originLabel);
          el.appendChild(originMarker);
          console.log(`🅰️ STEP 2: Enhanced ORIGIN marker created: "${displayLabel}" (36px, z-index 1000)`);
          break;
          
        case 'destination':
          // STEP 2 FIX: Enhanced destination marker with guaranteed label visibility
          el.className = 'labeled-marker-container step2-destination-marker';
          el.style.cssText = `
            position: relative;
            width: 36px;
            height: 36px;
            z-index: 1000;
          `;

          // Create the marker circle with enhanced visibility
          const destMarker = document.createElement('div');
          destMarker.className = 'custom-marker destination-marker';
          destMarker.style.cssText = `
            width: 36px;
            height: 36px;
            background: #dc2626;
            border: 3px solid white;
            border-radius: 50%;
            box-shadow: 0 4px 8px rgba(0,0,0,0.4), 0 0 0 1px rgba(220,38,38,0.3);
            cursor: pointer;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
          `;

          // Create the label element with maximum visibility
          const destLabel = document.createElement('div');
          destLabel.className = 'marker-label destination-label';
          destLabel.innerText = displayLabel;
          destLabel.style.cssText = `
            color: white;
            font-size: 18px;
            font-weight: 900;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
            text-align: center;
            pointer-events: none;
            user-select: none;
            line-height: 1;
            text-shadow: 0 1px 2px rgba(0,0,0,0.8);
            letter-spacing: 0.5px;
            z-index: 1001;
            position: relative;
          `;

          // Combine elements
          destMarker.appendChild(destLabel);
          el.appendChild(destMarker);
          console.log(`🅱️ STEP 2: Enhanced DESTINATION marker created: "${displayLabel}" (36px, z-index 1000)`);
          break;
        case 'waypoint':
          // STEP 2 FIX: Enhanced intermediate waypoint marker
          el.className = 'waypoint-marker step2-waypoint-marker';
          el.style.cssText = `
            width: 28px;
            height: 28px;
            background: #2563eb;
            border: 3px solid white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 14px;
            font-weight: 900;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
            text-align: center;
            line-height: 1;
            text-shadow: 0 1px 2px rgba(0,0,0,0.8);
            box-shadow: 0 3px 6px rgba(0,0,0,0.4), 0 0 0 1px rgba(37,99,235,0.3);
            cursor: pointer;
            z-index: 999;
            position: relative;
          `;
          el.innerText = displayLabel;
          console.log(`🔢 STEP 2: Enhanced WAYPOINT marker created: "${displayLabel}" (28px)`);
          break;
        case 'manual':
          el.style.cssText = `
            width: 24px;
            height: 24px;
            background: #dc2626;
            border: 2px solid white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 12px;
            font-weight: bold;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            cursor: pointer;
          `;
          el.innerText = String(index + 1);
          break;
      }
    } else {
      // Manual waypoint
      el.style.cssText = `
        width: 24px;
        height: 24px;
        background: #dc2626;
        border: 2px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 12px;
        font-weight: bold;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        cursor: pointer;
      `;
      el.innerText = String(index + 1);
    }

    // Add click handler to remove waypoint (except origin/destination)
    if ('type' in waypoint && (waypoint.type === 'waypoint' || waypoint.type === 'manual')) {
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        removeWaypoint(waypoint.id);
      });
    }

    const marker = new mapboxgl.Marker({
      element: el,
      anchor: 'center' // Center anchor works better for circular markers
    })
      .setLngLat(coords)
      .addTo(map);

    console.log(`✅ STEP 2: Created ${displayType} marker at [${coords[0].toFixed(4)}, ${coords[1].toFixed(4)}] with label "${displayLabel}"`);
    console.log(`📍 STEP 2: Marker element classes: ${el.className}`);
    console.log(`🎨 STEP 2: Marker element styles applied successfully`);

    // STEP 2 DEBUG: Verify marker is visible
    setTimeout(() => {
      const markerElement = el.querySelector('.marker-label, .origin-label, .destination-label');
      if (markerElement) {
        console.log(`✅ STEP 2 SUCCESS: Label "${displayLabel}" confirmed visible in DOM`);
      } else {
        console.warn(`⚠️ STEP 2 WARNING: Label might not be visible for "${displayLabel}"`);
      }
    }, 100);

    return marker;
  }, [map]);

  // Remove a waypoint
  const removeWaypoint = useCallback((waypointId: string) => {
    setWaypoints(prev => prev.filter(w => w.id !== waypointId));
    setManualWaypoints(prev => prev.filter(w => w.id !== waypointId));
    toast.success('Waypoint removed');
  }, []);

  // Undo last waypoint (Komoot-style)
  const undoLastWaypoint = useCallback(() => {
    setWaypoints(prev => {
      if (prev.length === 0) return prev;
      const updated = prev.slice(0, -1);
      // Make the new last point the destination (if more than 1 remain)
      if (updated.length > 1) {
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          type: 'destination'
        };
      }
      toast.success('Last point removed');
      return updated;
    });
  }, []);

  // Add a new waypoint at clicked location
  const addWaypointAtLocation = useCallback(async (lngLat: { lng: number; lat: number }) => {
    console.log('addWaypointAtLocation called with:', lngLat);
    const coords: [number, number] = [lngLat.lng, lngLat.lat];
    
    // Use ref to check current mode
    const { isAddingMode: addMode, isManualMode: manualMode } = modesRef.current;
    console.log('Current modes in addWaypoint:', { addMode, manualMode });
    
    // Get place name (or use coordinates as fallback)
    let placeName = `Point at ${coords[0].toFixed(4)}, ${coords[1].toFixed(4)}`;
    try {
      placeName = await reverseGeocode(coords);
    } catch (err) {
      console.warn('Reverse geocoding failed, using coordinates');
    }
    
    if (manualMode) {
      // Add manual waypoint
      const newWaypoint: ManualWaypoint = {
        id: `manual-${Date.now()}`,
        latitude: lngLat.lat,
        longitude: lngLat.lng,
        order: 0, // Will be updated in setState
        isLocked: true,
        name: placeName
      };
      
      setManualWaypoints(prev => {
        newWaypoint.order = prev.length;
        const updated = [...prev, newWaypoint];
        toast.success(`POI marked: ${updated.length}`);
        console.log('Added manual waypoint:', newWaypoint);
        return updated;
      });
    } else {
      // Add regular waypoint (Komoot-style: last click is always destination)
      setWaypoints(prev => {
        const order = prev.length;

        if (order === 0) {
          // First click = origin
          const newWaypoint: Waypoint = {
            id: `waypoint-${Date.now()}`,
            coords, name: placeName, type: 'origin',
            order: 0, address: placeName
          };
          toast.success('Start point set');
          return [newWaypoint];
        }

        // Subsequent clicks: retype previous destination as intermediate waypoint
        const retyped = prev.map(w =>
          w.type === 'destination' ? { ...w, type: 'waypoint' as const } : w
        );
        const newWaypoint: Waypoint = {
          id: `waypoint-${Date.now()}`,
          coords, name: placeName, type: 'destination',
          order, address: placeName
        };
        toast.success(`Point ${retyped.length + 1} added`);
        return [...retyped, newWaypoint];
      });
    }
  }, []);

  // Draw route on map (casing + fill pattern for road-hugging visual)
  const drawRoute = useCallback((coordinates: [number, number][], isRoadFollowing: boolean = false) => {
    if (!map || coordinates.length < 2) return;

    const sourceId = routeLayerRef.current;
    const casingId = `${sourceId}-casing`;
    const lineColor = isRoadFollowing ? '#10b981' : '#3b82f6';
    const casingColor = isRoadFollowing ? '#0d7a5f' : '#1e40af';

    const routeData: GeoJSON.Feature = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates
      }
    };

    // Update existing source if it exists (no flicker)
    const existingSource = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
    if (existingSource) {
      existingSource.setData(routeData);
      // Update colors
      if (map.getLayer(casingId)) {
        map.setPaintProperty(casingId, 'line-color', casingColor);
      }
      if (map.getLayer(sourceId)) {
        map.setPaintProperty(sourceId, 'line-color', lineColor);
      }
      return;
    }

    // First time: create source and both layers

    // Find the first symbol layer — insert route BELOW labels
    const layers = map.getStyle()?.layers;
    let firstSymbolId: string | undefined;
    if (layers) {
      for (const layer of layers) {
        if (layer.type === 'symbol') {
          firstSymbolId = layer.id;
          break;
        }
      }
    }

    // Also remove any leftover Directions plugin route layers
    const pluginLayers = [
      'directions-route-line', 'directions-route-line-alt',
      'directions-route-line-casing', 'directions-route-line-casing-alt',
      'directions-origin-point', 'directions-destination-point',
      'directions-waypoint-point', 'directions-hover-point'
    ];
    pluginLayers.forEach(id => {
      try { if (map.getLayer(id)) map.removeLayer(id); } catch (_) {}
    });

    map.addSource(sourceId, {
      type: 'geojson',
      data: routeData
    });

    // Casing layer (wider dark outline — road-hugging visual effect)
    map.addLayer({
      id: casingId,
      type: 'line',
      source: sourceId,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': casingColor,
        'line-width': 10,
        'line-opacity': 0.4
      }
    }, firstSymbolId);

    // Main route line (narrower bright fill on top of casing)
    map.addLayer({
      id: sourceId,
      type: 'line',
      source: sourceId,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': lineColor,
        'line-width': 6,
        'line-opacity': 0.85
      }
    }, firstSymbolId);
  }, [map]);

  // Fetch directions from Mapbox API
  const fetchDirectionsRef = useRef<(waypointList: Waypoint[]) => Promise<void>>();
  
  fetchDirectionsRef.current = async (waypointList: Waypoint[]) => {
    if (waypointList.length < 2) {
      console.log('Not enough waypoints for directions:', waypointList.length);
      return;
    }
    
    // Clear any pending fetch
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    
    // Debounce the fetch to prevent rapid updates
    fetchTimeoutRef.current = setTimeout(async () => {
      console.log('Fetching directions for waypoints:', waypointList);
      setIsLoadingRoute(true);
      console.log('Set loading route to true');
      
      try {
      const directionsWaypoints = waypointList.map(wp => ({
        lng: wp.coords[0],
        lat: wp.coords[1],
        name: wp.name
      }));
      
      console.log('Calling getDirections with:', {
        waypoints: directionsWaypoints,
        profile: routeProfile
      });
      
      const response = await getDirections(directionsWaypoints, {
        profile: routeProfile,
        geometries: 'geojson',
        steps: true,
        overview: 'full'
      });
      
      console.log('Directions API response:', response);
      
      if (response && response.routes && response.routes.length > 0) {
        const route = response.routes[0];
        setCurrentRoute(route);
        
        // Draw the road-following route
        if (route.geometry && route.geometry.coordinates) {
          console.log('Drawing road-following route with', route.geometry.coordinates.length, 'points');
          drawRoute(route.geometry.coordinates, true);
          
          // Fetch elevation data for the route
          console.log('Fetching elevation data for route...');
          getElevationProfile(route.geometry.coordinates as [number, number][])
            .then(elevationPoints => {
              console.log('Got elevation data:', elevationPoints.length, 'points');
              setElevationData(elevationPoints);
            })
            .catch(err => {
              console.warn('Failed to get elevation data:', err);
              setElevationData([]);
            });
          
          // Show route stats
          toast.success(
            `Route: ${formatDistance(route.distance)} • ${formatDuration(route.duration)}`,
            { duration: 5000 }
          );
        } else {
          console.warn('Route geometry missing, falling back to straight line');
          const coords = waypointList.map(w => w.coords);
          drawRoute(coords, false);
          setElevationData([]);
        }
        
        return route;
      } else {
        console.warn('No routes in response, falling back to straight line');
        const coords = waypointList.map(w => w.coords);
        drawRoute(coords, false);
        toast.info('Using straight line route (directions not available)');
      }
    } catch (error) {
      console.error('Error fetching directions:', error);
      toast.error('Failed to get road directions, using straight line');
      // Fall back to straight line
      const coords = waypointList.map(w => w.coords);
      drawRoute(coords, false);
      } finally {
        setIsLoadingRoute(false);
        console.log('Set loading route to false');
      }
    }, 100); // 100ms debounce
  };
  
  const fetchDirections = useCallback((waypointList: Waypoint[]) => {
    return fetchDirectionsRef.current?.(waypointList);
  }, []);

  // Update route when waypoints change
  useEffect(() => {
    if (!map) return;

    console.log(`🔄 Updating waypoint markers: ${waypoints.length} regular + ${manualWaypoints.length} manual`);

    // Clear existing markers using the proper cleanup function
    clearMarkers();

    // Add markers for all regular waypoints
    waypoints.forEach((waypoint, index) => {
      const marker = addWaypointMarker(waypoint, index, waypoints.length);
      if (marker) {
        markersRef.current.push(marker);
      }
    });

    // Add manual waypoints (POIs)
    manualWaypoints.forEach((waypoint, index) => {
      const marker = addWaypointMarker(waypoint, index, manualWaypoints.length);
      if (marker) {
        markersRef.current.push(marker);
      }
    });

    console.log(`📍 Total markers on map: ${markersRef.current.length}`);

    // Fetch road-following directions if we have waypoints
    if (waypoints.length >= 2 && fetchDirectionsRef.current) {
      fetchDirectionsRef.current(waypoints);
    } else if (waypoints.length === 1) {
      // Clear route if only one waypoint
      const casingId = `${routeLayerRef.current}-casing`;
      if (map.getLayer(routeLayerRef.current)) map.removeLayer(routeLayerRef.current);
      if (map.getLayer(casingId)) map.removeLayer(casingId);
      if (map.getSource(routeLayerRef.current)) map.removeSource(routeLayerRef.current);
      setCurrentRoute(null);
    }

    // Notify parent of route update
    if (onRouteUpdate) {
      onRouteUpdate(waypoints);
    }
  }, [waypoints, manualWaypoints, map, clearMarkers, addWaypointMarker]); // Include cleanup functions

  // Notify parent of route updates in a separate effect
  useEffect(() => {
    if (onRouteUpdate) {
      onRouteUpdate(waypoints);
    }
  }, [waypoints, onRouteUpdate]);

  // Handle route profile changes ONLY
  useEffect(() => {
    // Only refetch if we have waypoints and profile actually changed
    if (waypoints.length >= 2 && fetchDirectionsRef.current) {
      console.log('Route profile changed to:', routeProfile);
      fetchDirectionsRef.current(waypoints);
    }
  }, [routeProfile]); // ONLY depend on routeProfile, not waypoints!

  // Handle map clicks
  useEffect(() => {
    // Use map prop directly instead of mapRef.current
    if (!map) {
      console.log('Map not available for waypoint clicks');
      return;
    }

    console.log('Setting up waypoint click handler, map loaded:', map.loaded());

    const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
      // Use ref to get current mode values
      const { isAddingMode: addMode, isManualMode: manualMode } = modesRef.current;
      const shouldAddWaypoint = addMode || manualMode;
      
      console.log('Map clicked!', {
        coords: e.lngLat,
        addMode,
        manualMode,
        shouldAdd: shouldAddWaypoint
      });
      
      if (!shouldAddWaypoint) {
        console.log('Not in adding mode, ignoring click');
        return;
      }
      
      addWaypointAtLocation(e.lngLat);
    };

    // Set up click handler
    const setupHandler = () => {
      // Remove any existing handler first
      map.off('click', handleMapClick);
      // Add the click handler
      map.on('click', handleMapClick);
      console.log('✅ Click handler registered successfully');
    };

    // Check if map is ready
    if (map.loaded()) {
      setupHandler();
    } else {
      console.log('Waiting for map to load before setting up click handler');
      const onLoad = () => {
        console.log('Map loaded event fired, setting up click handler');
        setupHandler();
      };
      map.once('load', onLoad);
      
      // Also try on idle as a backup
      const onIdle = () => {
        console.log('Map idle event fired, setting up click handler');
        setupHandler();
      };
      map.once('idle', onIdle);
      
      return () => {
        map.off('load', onLoad);
        map.off('idle', onIdle);
      };
    }

    // Cleanup function
    return () => {
      if (map) {
        console.log('Cleaning up click handler');
        map.off('click', handleMapClick);
      }
    };
  }, [map, addWaypointAtLocation]);

  // Load waypoints from an existing track
  const loadTrackWaypoints = useCallback((track: any) => {
    if (!track || !track.data) return;
    
    // Clear existing waypoints
    clearMarkers();
    setWaypoints([]);
    setManualWaypoints([]);
    
    // Extract points from track data
    const points = track.data.points || track.data;
    if (!Array.isArray(points) || points.length === 0) return;
    
    // Convert track points to waypoints
    const newWaypoints: Waypoint[] = points.slice(0, 10).map((point: any, index: number) => ({
      id: `waypoint-${Date.now()}-${index}`,
      coords: [point.lon || point.lng, point.lat] as [number, number],
      name: `Point ${index + 1}`,
      type: index === 0 ? 'origin' : index === points.length - 1 ? 'destination' : 'waypoint' as const,
      order: index,
      elevation: point.ele
    }));
    
    setWaypoints(newWaypoints);
    toast.success(`Loaded ${newWaypoints.length} waypoints from track`);
  }, [clearMarkers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('🧹 Cleaning up waypoint manager');
      
      // Clear any pending fetch timeouts
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        console.log('⏰ Cleared pending fetch timeout');
      }
      
      // Clear markers using proper cleanup function
      clearMarkers();
      
      // Note: We don't remove the route layer here because the map might be reused
      // The layer will be updated when the component remounts
    };
  }, [clearMarkers]);

  return {
    // State
    waypoints,
    manualWaypoints,
    origin,
    destination,
    isAddingMode,
    isManualMode,
    originLocked,
    destinationLocked,
    currentRoute,
    routeProfile,
    isLoadingRoute,
    elevationData,
    
    // Actions
    setWaypoints,
    setManualWaypoints,
    setOrigin,
    setDestination,
    setIsAddingMode,
    setIsManualMode,
    setOriginLocked,
    setDestinationLocked,
    setRouteProfile,
    addWaypointAtLocation,
    removeWaypoint,
    undoLastWaypoint,
    clearMarkers,
    drawRoute,
    loadTrackWaypoints,
    fetchDirections
  };
}